import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// import axios from 'axios';
import api from '../../api';
import { getApiErrorMessage } from '../../utils/apiError';

// const API_URL = 'https://trh-backend.clouxiplexi.com/api';

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  roles: [],
  permissions: [],
  isLoading: false,
  error: null,
  isAuthenticated: !!localStorage.getItem('token'),
  initialized: false,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post('/login', credentials);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Login failed'));
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post('/register', credentials);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Registration failed'));
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state.auth.token;

      if (token) {
        await api.post(
          '/logout',
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      return null;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Logout failed'));
    }
  }
);

export const fetchUser = createAsyncThunk(
  'auth/fetchUser',
  async (_, { getState, rejectWithValue }) => {
    try {
      // const state = getState();
      // const token = state.auth.token;

      // if (!token) {
      //   return rejectWithValue('No token available');
      // }

      // const response = await api.get('/user', {
      //   headers: {
      //     Authorization: `Bearer ${token}`,
      //   },
      // });
      const response = await api.get('/user');

      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch user'));
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetAuth: (state) => {
      state.user = null;
      state.token = null;
      state.roles = [];
      state.permissions = [];
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem('token');
      // delete axios.defaults.headers.common['Authorization'];
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.access_token;
        state.roles = action.payload.roles;
        state.permissions = action.payload.permissions;
        state.isAuthenticated = true;

        // 1. Save Token to Local Storage (Persistent)
        localStorage.setItem('token', action.payload.access_token);

        // 2. ✅ Set Active Session Flag (Cleared when browser closes)
        sessionStorage.setItem('is_active_session', 'true');
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })

      // Register
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = null; // don't auto-login
        state.token = null;
        state.roles = [];
        state.permissions = [];
        state.isAuthenticated = false; // stay logged out
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })

      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.roles = [];
        state.permissions = [];
        state.isAuthenticated = false;
        localStorage.removeItem('token');
        // delete axios.defaults.headers.common['Authorization'];
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.user = null;
        state.token = null;
        state.roles = [];
        state.permissions = [];
        state.isAuthenticated = false;
        localStorage.removeItem('token');
        // delete axios.defaults.headers.common['Authorization'];
      })

      // Fetch User
      .addCase(fetchUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.roles = action.payload.roles;
        state.permissions = action.payload.permissions;
        state.isAuthenticated = true;
        state.initialized = true;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.user = null;
        state.token = null;
        state.roles = [];
        state.permissions = [];
        state.isAuthenticated = false;
        state.initialized = true;
        localStorage.removeItem('token');
        // delete axios.defaults.headers.common['Authorization'];
      });
  },
});

export const { clearError, resetAuth } = authSlice.actions;

export const selectHasPermission = (state, permission) => state.auth.permissions.includes(permission);
export const selectHasRole = (state, role) => state.auth.roles.includes(role);
export const selectAuthState = (state) => state.auth;

export default authSlice.reducer;